import { useState, useEffect, useCallback, useMemo } from 'react';
import { useServices } from '@/di/precon-supporter';
import { CourseId } from '@/domain/precon-supporter/value-objects';
import type { LearningProgress } from '@/domain/precon-supporter/learning-progress';

/**
 * 学習進捗を管理するフック
 */
export function useLearningProgress(courseId: 'basic' | 'advanced') {
  const services = useServices();

  // サービスをメモ化して、不要な再レンダリングを防ぐ
  const {
    completeLectureUseCase,
    getLearningProgressUseCase,
    resetExaminationUseCase,
  } = useMemo(() => services, [services]);

  const [progress, setProgress] = useState<LearningProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // 進捗を取得
  const fetchProgress = useCallback(async () => {
    // SSR時は何もしない
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    // サービスがまだ初期化されていない場合は待機
    if (!getLearningProgressUseCase) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const courseIdObj = CourseId.fromString(courseId);

      const data = await getLearningProgressUseCase.execute({
        courseId: courseIdObj,
      });

      setProgress(data);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('進捗の取得に失敗しました')
      );
    } finally {
      setLoading(false);
    }
  }, [courseId, getLearningProgressUseCase]); // getLearningProgressUseCaseを依存関係に追加

  // 講座完了（動画の視聴が最後まで完了した時に呼び出す）
  const completeLecture = useCallback(
    async (lectureId: string) => {
      try {
        setError(null);

        const courseIdObj = CourseId.fromString(courseId);

        await completeLectureUseCase.execute({
          courseId: courseIdObj,
          lectureId,
        });

        // 🔧 最適化: IndexedDBから再取得せず、ローカルステートを直接更新
        // これにより、視聴済みバッジ部分のみが再レンダリングされる
        // 初回視聴時（progress === null）はIndexedDBから再取得する必要がある
        if (progress) {
          const updatedProgress = progress.completeLecture(lectureId);
          setProgress(updatedProgress);
        } else {
          // 初回視聴時: IndexedDBから進捗を再取得してUIに反映
          await fetchProgress();
        }
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('講座完了の記録に失敗しました');
        setError(error);
        throw error;
      }
    },
    [courseId, completeLectureUseCase, progress, fetchProgress]
  );

  // 講座の進捗情報を取得
  const getLectureProgress = useCallback(
    (lectureId: string) => {
      return progress?.getLectureProgress(lectureId) ?? null;
    },
    [progress]
  );

  // 講座が完了しているか確認
  const isLectureCompleted = useCallback(
    (lectureId: string) => {
      return progress?.isLectureCompleted(lectureId) ?? false;
    },
    [progress]
  );

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  // 修了テストに合格しているか判定
  const hasPassedExamination = useCallback(() => {
    return progress?.hasPassedExamination() ?? false;
  }, [progress]);

  // 修了テスト合格状態をリセット（期限切れなどの場合）
  const resetExamination = useCallback(async () => {
    // SSR時は何もしない
    if (typeof window === 'undefined' || !resetExaminationUseCase) {
      return;
    }

    try {
      setError(null);

      const courseIdObj = CourseId.fromString(courseId);

      await resetExaminationUseCase.execute({
        courseId: courseIdObj,
      });

      // 進捗をリフレッシュ
      await fetchProgress();
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('試験状態のリセットに失敗しました');
      setError(error);
      throw error;
    }
  }, [courseId, resetExaminationUseCase, fetchProgress]);

  // 返り値をメモ化して、必要なときだけ新しいオブジェクトを返す
  return useMemo(
    () => ({
      progress,
      loading,
      error,
      completeLecture,
      getLectureProgress,
      isLectureCompleted,
      hasPassedExamination,
      resetExamination,
      refresh: fetchProgress,
    }),
    [
      progress,
      loading,
      error,
      completeLecture,
      getLectureProgress,
      isLectureCompleted,
      hasPassedExamination,
      resetExamination,
      fetchProgress,
    ]
  );
}
